import {
  Autocomplete,
  Button,
  type Item,
  SelectInput,
  SelectInputItem,
  TextField,
} from '@ads/components-react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import { useClassStudentsDropdown } from '../../../hooks/useClassStudentsDropdown';
import { useCompaniesDropdown } from '../../../hooks/useCompaniesDropdown';
import { usePaymentFilters } from '../../../hooks/usePaymentList';
import { useStudentSearchDropdown } from '../../../hooks/useStudentSearchDropdown';

export function PaymentFilterBar() {
  const { t } = useTranslation('personManagement');
  const { t: tCommon } = useTranslation();
  const { filters, updateFilters, resetFilters } = usePaymentFilters();
  const [showMore, setShowMore] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const { companies } = useCompaniesDropdown();
  const { classes } = useClassStudentsDropdown();
  const { students, isQuerySuccess: isStudentQuerySuccess } =
    useStudentSearchDropdown(studentSearch);

  const studentItems = students.map(
    (s): Item<string> => ({ label: s.value ?? '', value: s.value ?? '' }),
  );
  const selectedStudent: Item<string> | undefined = filters.studentName
    ? { label: filters.studentName, value: filters.studentName }
    : undefined;
  const studentMessage = getAutocompleteSearchMessage(
    studentSearch.length,
    isStudentQuerySuccess,
    studentItems.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <div className="flex flex-col gap-mini">
      {/* Row 1 */}
      <div className="grid grid-cols-1 gap-xs sm:grid-cols-2 lg:grid-cols-4">
        <SelectInput
          label={t('paymentList.filters.company')}
          value={filters.company || 'all'}
          onValueChange={value => updateFilters({ company: value === 'all' ? '' : value })}
        >
          <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
            {t('paymentList.filters.all')}
          </SelectInputItem>
          {companies.map(c => (
            <SelectInputItem key={c.id} value={c.id} custom={{ root: 'text-nowrap' }}>
              {c.name}
            </SelectInputItem>
          ))}
        </SelectInput>
        <SelectInput
          label={t('paymentList.filters.class')}
          value={filters.classId || 'all'}
          onValueChange={value => updateFilters({ classId: value === 'all' ? '' : value })}
        >
          <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
            {t('paymentList.filters.all')}
          </SelectInputItem>
          {classes.map(c => (
            <SelectInputItem key={c.id} value={c.id} custom={{ root: 'text-nowrap' }}>
              {c.name}
            </SelectInputItem>
          ))}
        </SelectInput>
        <TextField
          label={t('paymentList.filters.cpf')}
          placeholder="000.000.000-00"
          fullWidth
          size="md"
          value={filters.cpf}
          onChange={e => updateFilters({ cpf: e.target.value })}
        />
        <Autocomplete<string>
          label={t('paymentList.filters.studentName')}
          placeholder={t('paymentList.filters.studentPlaceholder')}
          size="md"
          list={studentItems}
          {...(studentMessage !== undefined ? { notFoundMessage: studentMessage } : {})}
          selectedItem={selectedStudent ?? { label: '', value: '' }}
          onChange={e => setStudentSearch(e.target.value)}
          handleChange={item => {
            updateFilters({ studentName: item.value ?? '' });
            setStudentSearch('');
          }}
          handleClearQuery={() => {
            updateFilters({ studentName: '' });
            setStudentSearch('');
          }}
        />
      </div>

      {/* Row 2 - expandable */}
      {showMore && (
        <div className="grid grid-cols-1 gap-xs sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            label={t('paymentList.filters.nossoNumero')}
            fullWidth
            size="md"
            placeholder={t('paymentList.filters.nossoNumero')}
            value={filters.nossoNumero}
            onChange={e => updateFilters({ nossoNumero: e.target.value })}
          />
          <TextField
            label={t('paymentList.filters.thirdPartyCpf')}
            placeholder="000.000.000-00"
            fullWidth
            size="md"
            value={filters.thirdPartyCpf}
            onChange={e => updateFilters({ thirdPartyCpf: e.target.value })}
          />
          <TextField
            label={t('paymentList.filters.thirdPartyCnpj')}
            placeholder="00.000.000/0000-00"
            fullWidth
            size="md"
            value={filters.thirdPartyCnpj}
            onChange={e => updateFilters({ thirdPartyCnpj: e.target.value })}
          />
          <TextField
            label={t('paymentList.filters.email')}
            placeholder="email@exemplo.com"
            fullWidth
            size="md"
            value={filters.email}
            onChange={e => updateFilters({ email: e.target.value })}
          />
        </div>
      )}

      {/* Row 3 - method + actions */}
      <div className="flex flex-wrap items-end gap-xs">
        <div className="w-full sm:w-48">
          <SelectInput
            label={t('paymentList.filters.paymentMethod')}
            value={filters.paymentMethod}
            onValueChange={value =>
              updateFilters({
                paymentMethod: value as typeof filters.paymentMethod,
              })
            }
          >
            <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
              {t('paymentList.filters.all')}
            </SelectInputItem>
            <SelectInputItem value="boleto" custom={{ root: 'text-nowrap' }}>
              {t('paymentMethod.boleto')}
            </SelectInputItem>
            <SelectInputItem value="credit_card" custom={{ root: 'text-nowrap' }}>
              {t('paymentMethod.credit_card')}
            </SelectInputItem>
            <SelectInputItem value="pix" custom={{ root: 'text-nowrap' }}>
              {t('paymentMethod.pix')}
            </SelectInputItem>
            <SelectInputItem value="transfer" custom={{ root: 'text-nowrap' }}>
              {t('paymentMethod.transfer')}
            </SelectInputItem>
          </SelectInput>
        </div>
        <div className="w-full sm:w-40">
          <SelectInput
            label={t('paymentList.filters.status')}
            value={filters.status}
            onValueChange={value => updateFilters({ status: value as typeof filters.status })}
          >
            <SelectInputItem value="all" custom={{ root: 'text-nowrap' }}>
              {t('paymentList.filters.all')}
            </SelectInputItem>
            <SelectInputItem value="active" custom={{ root: 'text-nowrap' }}>
              {t('status.active')}
            </SelectInputItem>
            <SelectInputItem value="inactive" custom={{ root: 'text-nowrap' }}>
              {t('status.inactive')}
            </SelectInputItem>
            <SelectInputItem value="completed" custom={{ root: 'text-nowrap' }}>
              {t('status.completed')}
            </SelectInputItem>
            <SelectInputItem value="cancelled" custom={{ root: 'text-nowrap' }}>
              {t('status.cancelled')}
            </SelectInputItem>
            <SelectInputItem value="overdue" custom={{ root: 'text-nowrap' }}>
              {t('status.overdue')}
            </SelectInputItem>
          </SelectInput>
        </div>
        <Button
          hierarchy="tertiary"
          size="lg"
          leadingIcon={showMore ? ChevronUpIcon : ChevronDownIcon}
          onClick={() => setShowMore(prev => !prev)}
        >
          {showMore ? t('paymentList.filters.lessFilters') : t('paymentList.filters.moreFilters')}
        </Button>
        <Button hierarchy="secondary" size="lg" onClick={resetFilters}>
          {t('paymentList.filters.clear')}
        </Button>
      </div>
    </div>
  );
}
